import React, { useEffect, useState, useCallback, useMemo } from "react";
import { SlLike, SlDislike } from "react-icons/sl";
import { PiArrowBendDownRightBold } from "react-icons/pi";
import ReplyModal from "./ReplyModal";
import axios from "axios";
import styles from "./ReviewForm.module.css";

const RATING_VALUES = [5, 4, 3, 2, 1];
const INITIAL_VISIBLE_REVIEWS = 4;
const REVIEWS_LOAD_STEP = 4;

const ReviewForm = ({ productCode }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [email, setEmail] = useState("");
  const [reviews, setReviews] = useState([]);
  const [visibleReviews, setVisibleReviews] = useState([]);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_REVIEWS);
  const [activeReplyReviewId, setActiveReplyReviewId] = useState(null);
  const [expandedReplies, setExpandedReplies] = useState({});
  const [userKey] = useState(() => {
    let key = window.localStorage.getItem("userKey");
    if (!key) {
      key = Math.random().toString(36).substr(2, 9); // генеруємо випадковий ключ
      window.localStorage.setItem("userKey", key);
    }
    return key;
  });
  const [isLoading, setIsLoading] = useState(false);

  const fetchReviews = useCallback(async () => {
    try {
      const res = await axios.get(`/api/reviews/${productCode}`);
      const nextReviews = Array.isArray(res.data) ? res.data : [];
      setReviews(nextReviews);
      setVisibleCount(INITIAL_VISIBLE_REVIEWS);
      setVisibleReviews(nextReviews.slice(0, INITIAL_VISIBLE_REVIEWS));
    } catch (err) {
      console.error("Не вдалося завантажити відгуки:", err);
    }
  }, [productCode]);


  useEffect(() => {
    if (productCode) {
      fetchReviews();
    }
  }, [productCode, fetchReviews]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) return alert("Будь ласка, поставте оцінку товару.");
    try {
      await axios.post(`/api/reviews/${productCode}`, {
        rating,
        review: reviewText,
        author: authorName || "Анонім",
        email: email || "",
      });
      setRating(0);
      setReviewText("");
      setAuthorName("");
      setEmail("");
      await fetchReviews();
    } catch (err) {
      console.error("Помилка при відправці відгуку:", err);
    }
  };

  const handleLoadMore = () => {
    const newCount = visibleCount + REVIEWS_LOAD_STEP;
    setVisibleCount(newCount);
    setVisibleReviews(reviews.slice(0, newCount));
  };

  const toggleReplies = (id) => {
    setExpandedReplies((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const monthsGenitive = [
    "січня",
    "лютого",
    "березня",
    "квітня",
    "травня",
    "червня",
    "липня",
    "серпня",
    "вересня",
    "жовтня",
    "листопада",
    "грудня",
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = monthsGenitive[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const handleLike = async (reviewId) => {
    setIsLoading(true);
    try {
      const res = await axios.post(`/api/reviews/${reviewId}/like`, {
        userKey,
      });
      setReviews((prev) =>
        prev.map((r) =>
          r._id === reviewId
            ? {
                ...r,
                likes: res.data.likes,
                dislikes: res.data.dislikes,
                likesBy: res.data.likesBy,
                dislikesBy: res.data.dislikesBy,
              }
            : r
        )
      );
    } catch (err) {
      console.error("Помилка при лайкуванні:", err);
    }
    setIsLoading(false);
  };

  const handleDislike = async (reviewId) => {
    setIsLoading(true);
    try {
      const res = await axios.post(`/api/reviews/${reviewId}/dislike`, {
        userKey,
      });
      setReviews((prev) =>
        prev.map((r) =>
          r._id === reviewId
            ? {
                ...r,
                likes: res.data.likes,
                dislikes: res.data.dislikes,
                likesBy: res.data.likesBy,
                dislikesBy: res.data.dislikesBy,
              }
            : r
        )
      );
    } catch (err) {
      console.error("Помилка при дизлайкуванні:", err);
    }
    setIsLoading(false);
  };
  
  const handleReplyLike = async (reviewId, replyIndex) => {
    setIsLoading(true);
    try {
      const res = await axios.post(
        `/api/reviews/${reviewId}/reply/${replyIndex}/like`,
        { userKey }
      );
      setReviews((prev) =>
        prev.map((r) =>
          r._id === reviewId
            ? {
                ...r,
                replies: r.replies.map((rep, idx) =>
                  idx === replyIndex
                    ? {
                        ...rep,
                        likes: res.data.likes,
                        dislikes: res.data.dislikes,
                        likesBy: res.data.likesBy,
                        dislikesBy: res.data.dislikesBy,
                      }
                    : rep
                ),
              }
            : r
        )
      );
    } catch (err) {
      console.error("Помилка при лайкуванні відповіді:", err);
    }
    setIsLoading(false);
  };
  
  const handleReplyDislike = async (reviewId, replyIndex) => {
    setIsLoading(true);
    try {
      const res = await axios.post(
        `/api/reviews/${reviewId}/reply/${replyIndex}/dislike`,
        { userKey }
      );
      setReviews((prev) =>
        prev.map((r) =>
          r._id === reviewId
            ? {
                ...r,
                replies: r.replies.map((rep, idx) =>
                  idx === replyIndex
                    ? {
                        ...rep,
                        likes: res.data.likes,
                        dislikes: res.data.dislikes,
                        likesBy: res.data.likesBy,
                        dislikesBy: res.data.dislikesBy,
                      }
                    : rep
                ),
              }
            : r
        )
      );
    } catch (err) {
      console.error("Помилка при дизлайкуванні відповіді:", err);
    }
    setIsLoading(false);
  };
  
  useEffect(() => {
    setVisibleReviews(reviews.slice(0, visibleCount));
  }, [reviews, visibleCount]);

  const ratingSummary = useMemo(() => {
    const validRatings = reviews
      .map((review) => Number(review?.rating))
      .filter((reviewRating) => Number.isFinite(reviewRating));
    const totalRating = validRatings.reduce((sum, reviewRating) => {
      return sum + reviewRating;
    }, 0);
    const averageRating =
      validRatings.length > 0 ? totalRating / validRatings.length : 0;

    return {
      average: averageRating.toFixed(1),
      count: validRatings.length,
    };
  }, [reviews]);

  const ratingStats = useMemo(() => {
    const total = Array.isArray(reviews) ? reviews.length : 0;
    const counts = RATING_VALUES.reduce((acc, value) => {
      acc[value] = 0;
      return acc;
    }, {});

    reviews.forEach((review) => {
      const reviewRating = Math.round(Number(review?.rating));
      if (counts[reviewRating] !== undefined) {
        counts[reviewRating] += 1;
      }
    });

    return RATING_VALUES.map((value) => ({
      value,
      count: counts[value],
      percentage: total > 0 ? (counts[value] / total) * 100 : 0,
    }));
  }, [reviews]);

  const renderRatingSummaryRow = () => (
    <div className={styles.ratingSummaryRow}>
      Оцінка{" "}
      <strong className={styles.ratingAverageValue}>
        {ratingSummary.average}
      </strong>
      /5{" "}
      на основі відгуків {ratingSummary.count} клієнтів
    </div>
  );

  const renderRatingStatistics = () => (
    <div className={styles.ratingStatistics} aria-label="Статистика оцінок">
      {ratingStats.map(({ value, count, percentage }) => (
        <div key={value} className={styles.ratingStatisticRow}>
          <span className={styles.ratingStatisticLabel}>
            {value}
            <span className={styles.ratingStatisticStarIcon} aria-hidden="true">
              ★
            </span>
          </span>
          <span className={styles.ratingProgressTrack} aria-hidden="true">
            <span
              className={styles.ratingProgressFill}
              style={{ width: `${percentage}%` }}
            />
          </span>
          <span className={styles.ratingStatisticCount}>{count}</span>
        </div>
      ))}
    </div>
  );

  const renderRatingSelector = () => (
    <div className={styles.stars} aria-label="Оберіть оцінку від 1 до 5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className={`${styles.star} ${
            star <= (hoverRating || rating) ? styles.filled : ""
          }`}
          onMouseEnter={() => setHoverRating(star)}
          onMouseLeave={() => setHoverRating(0)}
          onFocus={() => setHoverRating(star)}
          onBlur={() => setHoverRating(0)}
          onClick={() => setRating(star)}
          aria-label={`Оцінка ${star} з 5`}
          aria-pressed={rating === star}
        >
          ★
        </button>
      ))}
    </div>
  );
  
  return (
    <div className={styles.reviewRow}>
      <div className={styles.formWrapper}>
        <div className={styles.reviewFormCombined}>
          <div className={styles.reviewFormCombinedContent}>
            <form className={styles.reviewForm} onSubmit={handleSubmit}>
              {renderRatingSummaryRow()}
              {renderRatingStatistics()}
              {renderRatingSelector()}
              <h3>Залишити відгук</h3>
              <input
                className={styles.input}
                type="text"
                placeholder="Ваше ім'я (необов'язково)"
                value={authorName}
                onChange={(e) => setAuthorName(e.target.value)}
              />
              <input
                className={styles.input}
                type="email"
                placeholder="Ваш email (необов'язково)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <textarea
                className={styles.textarea}
                placeholder="Ваш відгук..."
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
              ></textarea>
              <button className={styles.submitButton} type="submit">
                Надіслати відгук
              </button>
            </form>
          </div>
        </div>
      </div>

      <div className={styles.reviewsWrapper}>
        <h3>Відгуки покупців</h3>
        {visibleReviews.length > 0 ? (
          visibleReviews.map((r) => (
            <React.Fragment key={r._id}>
              {/* Блок відгуку */}
              <div className={styles.reviewItem}>
                <div className={styles.reviewHeaderSmall}>
                  <strong>{r.author || "Анонім"}</strong>
                  <span>{formatDate(r.createdAt)}</span>
                </div>
                <div className={styles.reviewContentSmall}>
                  <div className={styles.reviewRatingSmall}>
                    Оцінка: {r.rating} / 5
                  </div>
                  <div>{r.review}</div>
                </div>
                <div className={styles.reviewFooter}>
                  <div className={styles.actionsLeft}>
                    <span
                      className={`${styles.thumb} ${
                        r.likesBy.includes(userKey)
                          ? styles.thumbLike
                          : styles.thumbNeutral
                      }`}
                      onClick={() => !isLoading && handleLike(r._id)}
                      style={{
                        pointerEvents: isLoading ? "none" : "auto",
                        opacity: isLoading ? 0.5 : 1,
                      }}
                    >
                      <SlLike className={styles.thumbIcon} /> {r.likes || 0}
                    </span>
                    <span
                      className={`${styles.thumb} ${
                        r.dislikesBy.includes(userKey)
                          ? styles.thumbDislike
                          : styles.thumbNeutral
                      }`}
                      onClick={() => !isLoading && handleDislike(r._id)}
                      style={{
                        pointerEvents: isLoading ? "none" : "auto",
                        opacity: isLoading ? 0.5 : 1,
                      }}
                    >
                      <SlDislike className={styles.thumbIcon} />{" "}
                      {r.dislikes || 0}
                    </span>
                  </div>
                  <button
                    className={styles.replyButton}
                    onClick={() => setActiveReplyReviewId(r._id)}
                  >
                    <PiArrowBendDownRightBold /> Відповісти
                  </button>
                </div>

                {/* Кнопка перегляду відповідей всередині відгуку */}
                {r.replies && r.replies.length > 0 && (
                  <button
                    onClick={() => toggleReplies(r._id)}
                    className={styles.loadMoreButton}
                  >
                    {expandedReplies[r._id]
                      ? "Сховати відповіді"
                      : `Переглянути відповіді (${r.replies.length})`}
                  </button>
                )}
              </div>

              {/* Відповіді — ПОЗА рамкою відгуку */}
              {expandedReplies[r._id] &&
                r.replies.map((rep, idx) => (
                  <div key={idx} className={styles.replyItem}>
                    <div className={styles.reviewHeaderSmall}>
                      <strong>{rep.author}</strong>
                      <span>{formatDate(rep.createdAt)}</span>
                    </div>
                    <div className={styles.reviewContentSmall}>{rep.text}</div>
                    <div className={styles.reviewFooter}>
                      <span
                        className={`${styles.thumb} ${
                          rep.likesBy.includes(userKey)
                            ? styles.thumbLike
                            : styles.thumbNeutral
                        }`}
                        onClick={() =>
                          !isLoading && handleReplyLike(r._id, idx)
                        }
                        style={{
                          pointerEvents: isLoading ? "none" : "auto",
                          opacity: isLoading ? 0.5 : 1,
                        }}
                      >
                        <SlLike className={styles.thumbIcon} /> {rep.likes || 0}
                      </span>
                      <span
                        className={`${styles.thumb} ${
                          rep.dislikesBy.includes(userKey)
                            ? styles.thumbDislike
                            : styles.thumbNeutral
                        }`}
                        onClick={() =>
                          !isLoading && handleReplyDislike(r._id, idx)
                        }
                        style={{
                          pointerEvents: isLoading ? "none" : "auto",
                          opacity: isLoading ? 0.5 : 1,
                        }}
                      >
                        <SlDislike className={styles.thumbIcon} />{" "}
                        {rep.dislikes || 0}
                      </span>
                    </div>
                  </div>
                ))}
            </React.Fragment>
          ))
        ) : (
          <p className={styles.noReviews}>Поки немає відгуків</p>
        )}
        {reviews.length > visibleReviews.length && (
          <button className={styles.loadMoreButton} onClick={handleLoadMore}>
            Завантажити ще відгуки
          </button>
        )}
        <ReplyModal
          isOpen={!!activeReplyReviewId}
          onClose={() => setActiveReplyReviewId(null)}
          reviewId={activeReplyReviewId}
          onReplySuccess={fetchReviews}
        />
      </div>
    </div>
  );
};

export default ReviewForm;
